import React from 'react';
import { cloneDeep } from 'lodash';
import { assign } from 'xstate';
import { IntlShape } from 'react-intl';

import { MachineContext } from '../../MachineContext';
import { BaseComponent } from '../Component';
import { translateWithFallback } from '../../util';
import { CoachMessage } from '../CoachMessage';

import { MultipleOutput, MultipleOutputProps } from './MultipleOutput';

export interface MultipleOutputCompositeProps extends MultipleOutputProps {
  message: string;
}

export interface MultipleOutputCompositeComponent extends BaseComponent {
  type: 'multipleOutputComposite';
  props: MultipleOutputCompositeProps;
}

export const MultipleOutputComposite: React.FC<MultipleOutputCompositeProps> = ({
  elements,
  disableDrag,
  maxItems,
  minItems,
  selectAll,
  hideDate = false,
  hideSelect = false,
  iconType,
  buttonText,
  saveResultTo,
  message,
  toolTipWarningMessage,
}) => {
  return (
    <>
      <div style={{ marginBottom: '1rem' }}>
        <CoachMessage message={message} />
      </div>
      <MultipleOutput
        elements={elements}
        disableDrag={disableDrag}
        maxItems={maxItems}
        minItems={minItems}
        selectAll={selectAll}
        hideDate={hideDate}
        hideSelect={hideSelect}
        iconType={iconType}
        buttonText={buttonText}
        saveResultTo={saveResultTo}
        toolTipWarningMessage={toolTipWarningMessage}
      />
    </>
  );
};

export const renderMultipleOutputCompositeAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );

    const payload = actionMetadata.action.payload;
    const hideDate =
      payload.hideDate &&
      payload.hideDate !== undefined &&
      payload.hideDate !== null
        ? payload.hideDate
        : false;
    const hideSelect =
      payload.hideSelect &&
      payload.hideSelect !== undefined &&
      payload.hideSelect !== null
        ? payload.hideSelect
        : false;

    const dataSourceList = payload.getValuesFrom;

    // if getValuesFrom is set, then use it to retrieve elements
    // if not, retrieve elements directly from payload
    const elements =
      dataSourceList && Array.isArray(dataSourceList)
        ? dataSourceList
            .filter(
              (datasource: string) =>
                datasource !== undefined &&
                datasource !== null &&
                datasource.trim() !== '' &&
                context.userData[datasource] !== undefined
            )
            .map((dataSource: string) => {
              const originalData = context.userData[dataSource];

              // ! if adapt anything here, consider adapting it in
              // ! MultipleOutput.tsx, too
              return originalData.map((entry: any) => {
                if (typeof entry === 'string') {
                  return {
                    type: 'undefined',
                    value: entry,
                  };
                } else {
                  // added array handling with PROD-1623
                  if (Array.isArray(entry)) {
                    if (typeof entry[0] === 'string') {
                      return entry.map((singleEntry: string) => {
                        return {
                          type: 'undefined',
                          value: singleEntry,
                        };
                      });
                    } else {
                      return entry;
                    }
                  } else {
                    return {
                      ...entry,
                      date: entry.date ?? null,
                      starred: false, // PROD-1277, prev.: !!entry.starred,
                    };
                  }
                }
              });
            })
            .flat() // PROD-1623
        : payload.elements;

    newContext.widgetData.push({
      type: 'multipleOutputComposite',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        elements: elements,
        saveResultTo: payload.saveResultTo,
        buttonText: translateWithFallback(intl, payload.buttonText),
        disableDrag: payload.disableDrag,
        maxItems: payload.maxItems,
        minItems: payload.minItems,
        selectAll: payload.selectAll,
        hideDate: hideDate,
        hideSelect: hideSelect,
        iconType: payload.iconType,
        message: translateWithFallback(intl, payload.message),
        toolTipWarningMessage: translateWithFallback(
          intl,
          payload.toolTipWarningMessage
        ),
      },
    });
    return newContext;
  });
};
